@if(currentSection === 'distributionSettings') {
<div class="flex-1 px-300-rem py-250-rem">
  <div>
    <div class="flex flex-col space-y-050-rem">
      <h1 class="text-headline-md-weight-medium text-action-fg-secondary">Distribution settings</h1>
      <p class="text-container-fg-muted text-body-md-weight-regular w-5xl">
        Set the distribution strategy for this update. You can either set the rollout percentage of
        each target product group that will receive the update via this rollout plan or define the
        devices to roll out to individually by addressing their Bt-friendly name.
      </p>
    </div>
    <div class="border rounded-050 mt-3 p-100-rem border-container-border-default max-w-236.5">
      <h3 class="text-label-lg-weight-medium text-container-fg-default">Distibution strategy</h3>

      <mat-button-toggle-group
        name="mat-button-toggle-group"
        aria-label="distribution strategy"
        class="*:w-48 *:text-body-md-weight-regular"
        [disabled]="isEditingRolloutPlan()"
        [value]="distributionStrategy()"
        (change)="distributionStrategy.set($event.value)">
        <mat-button-toggle value="Percentage">Product group</mat-button-toggle>
        <mat-button-toggle value="Whitelist">Device list</mat-button-toggle>
      </mat-button-toggle-group>
    </div>

    @if(distributionStrategy() === 'Percentage') {
    <div
      class="border rounded-050 mt-6 bg-container-bg-default border-container-border-default max-w-236.5">
      <div class="p-100-rem">
        <h3 class="text-label-lg-weight-medium text-container-fg-default">
          Product group specific rollout percentages
        </h3>
        <div class="flex gap-x-025-rem py-100-em w-180">
          <i
            class="icon_marina_ui_InfoOutlined text-container-fg-muted text-headline-sm"
            aria-hidden="true"></i>
          <p class="text-container-fg-muted text-body-lg-weight-regular">
            100% includes all current devices and any future compatible devices in the selected
            product groups, while the plan is active. If you don't select a value for a product
            group, 0 % will be set as its default.
          </p>
        </div>

        <div class="flex gap-x-025-rem pb-025-em w-180">
          <i
            class="icon_marina_ui_InfoOutlined text-container-fg-muted text-headline-sm"
            aria-hidden="true"></i>
          <p class="text-container-fg-muted text-body-lg-weight-regular">
            No automatic compatibility checks between firmware package and target product groups.
            Only set the rollout percentages for product groups that are compatible with the
            firmware package!
          </p>
        </div>
        <div class="flex flex-col space-y-050-rem max-w-177.5">
          <div class="grid grid-cols-2 py-100-rem space-y-100-em">
            @for (product_group of groupProducts(); track product_group.id){
            <div>
              <p class="text-label-sm-weight-regular text-container-fg-muted">
                Target product group
              </p>
              <p class="text-body-lg-weight-regular text-[1rem] text-container-fg-default">
                {{ product_group.name }}
              </p>
            </div>

            <mat-form-field>
              <mat-label>Rollout percentage [%]</mat-label>
              <mat-select
                [value]="selectedPercentages()[product_group.id]"
                (selectionChange)="onPercentageChange($event.value, product_group.id)">
                @for (percentage of getAvailablePercentages(product_group.id); track $index) {
                <mat-option [value]="percentage">{{ percentage }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
            }
          </div>
        </div>
      </div>
    </div>
    } @else {
    <div
      class="border rounded-050 mt-6 bg-container-bg-default border-container-border-default max-w-236.5">
      <div class="p-100-rem">
        <h3 class="text-label-lg-weight-medium text-container-fg-default">Devices to roll out to</h3>
        <div class="flex gap-x-025-rem py-2 items-center">
          <i
            class="icon_marina_ui_InfoOutlined text-container-fg-muted text-headline-sm"
            aria-hidden="true"></i>
          <p class="text-container-fg-muted text-body-lg-weight-regular">
            For rollout plans in the state "Active" devices to roll out to can only be added, not
            removed.
          </p>
        </div>

        @if(initialDevices().length > 0) {
        <div class="pl-2 py-100-rem">
          <p class="text-label-sm-weight-regular text-container-fg-muted mb-1">
            Bt-friendly name(s) of device(s)
          </p>
          <ul class="list-none m-0 p-0 flex flex-col gap-0.5">
            @for(device of initialDevices(); track device) {
            <li class="text-body-lg-weight-regular text-container-fg-default">{{ device }}</li>
            }
          </ul>
        </div>
        }

        <mat-form-field [ngClass]="{'error-border': showError() && error}" class="w-full mt-2">
          <mat-label>{{ 'Bt-friendly name(s) of device(s)' }}</mat-label>
          <textarea
            rows="1"
            matInput
            [formControl]="deviceListControl"
            placeholder="Bt-friendly name(s) of device(s)"
            cdkTextareaAutosize
            cdkAutosizeMinRows="1"
            cdkAutosizeMaxRows="10"></textarea>
        </mat-form-field>

        @if(showError() && error){
        <div
          class="flex text-label-sm-weight-regular gap-025-em items-center text-feedback-fg-error mt-025-em"
          [class.items-center]="(errorMessages?.length ?? 0) > 1"
          [class.items-start]="(errorMessages?.length ?? 0) < 1">
          <i class="icon_marina_ui_CrossContainedOutlined text-feedback-fg-error text-[1rem] mt-0.5 shrink-0"></i>
          @if(errorMessages){
            <ul class="list-none m-0 p-0 flex flex-col content-center gap-0.5">
              @for(msg of errorMessages; track msg){
                <li>{{ msg }}</li>
              }
            </ul>
          } @else if(errorMessage) {
            <span>{{ errorMessage }}</span>
          }
        </div>
        }
        @else {
        <p class="text-label-sm-weight-regular text-container-fg-muted py-1 max-w-2xl w-full">Please add the Bt-friendly names for all targeted devices separated by semi-colons, expected format: e.g. OB_3B5-4[4]_SN202414009;OB_3B5-4[4]_SN202414010</p>
        }
      </div>
    </div>
    }
  </div>
</div>
}
